import React, { useEffect } from "react";
import { RightPaginationIcon, LeftPaginationIcon } from "../ui/icons";
import { Form } from "react-bootstrap";
import Filter from "./filter";
import { TABLE_RENDERING_MODES } from "../../common/constants";
import {
  ExpandedState,
  useReactTable,
  ColumnFiltersState,
  getPaginationRowModel,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getExpandedRowModel,
  FilterFn,
  flexRender,
  getSortedRowModel,
  SortingState,
} from "@tanstack/react-table";

import { RankingInfo, rankItem } from "@tanstack/match-sorter-utils";

declare module "@tanstack/table-core" {
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
  }
  interface FilterMeta {
    itemRank: RankingInfo;
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);

  addMeta({
    itemRank,
  });

  return itemRank.passed;
};

interface IProps {
  data: any;
  columns: any;
  globalFilter?: string;
  setGlobalFilter?: React.Dispatch<React.SetStateAction<string>>;
  noDataMessage: string;
  newlyCreatedAlertId?: string;
  singleRowSelection?: boolean;
  assignSuccessfully?: boolean;
  setAssignSuccessfully?: React.Dispatch<React.SetStateAction<boolean>>;
  shouldResetSelection?: boolean;
  acknowledgeReset?: () => void;
  mode?: (typeof TABLE_RENDERING_MODES)[keyof typeof TABLE_RENDERING_MODES];
  totalCount?: number;
  pageIndex?: number;
  pageSize?: number;
  onPaginationChange?: (pageIndex: number, pageSize: number) => void;
  onColumnFiltersChange?: (columnFilters: ColumnFiltersState) => void;
  onGlobalFilterChange?: (globalFilter: string) => void;
  globalFilterFn?: (row: any, columnId: string, filterValue: string) => boolean;
  columnFilters?: ColumnFiltersState;
  getRowClassName?: (row: any) => string;
  defaultExpanded?: ExpandedState;
  resetExpandedRowsKey?: number;
  renderFiltersAboveTable?: boolean;

}

export const TableFilterBar = ({
  table,
}: {
  table: ReturnType<typeof useReactTable>;
}) => {
  const filterableHeaders =
    table.getHeaderGroups()[0]?.headers.filter((header) =>
      header.column.getCanFilter(),
    ) ?? [];

  if (filterableHeaders.length === 0) {
    return null;
  }

  const leadingFilters =
    filterableHeaders.length > 1
      ? filterableHeaders.slice(0, -1)
      : filterableHeaders;
  const trailingFilter =
    filterableHeaders.length > 1
      ? filterableHeaders[filterableHeaders.length - 1]
      : null;

  return (
    <div className="table-filter-bar">
      <div className="table-filter-bar-group">
        {leadingFilters.map((header) => (
          <div className="alerts-dropdown" key={header.id}>
            <Filter
              column={header.column}
              flatRows={table.getPreFilteredRowModel().flatRows}
            />
          </div>
        ))}
      </div>
      {trailingFilter && (
        <div className="alerts-dropdown" key={trailingFilter.id}>
          <Filter
            column={trailingFilter.column}
            flatRows={table.getPreFilteredRowModel().flatRows}
          />
        </div>
      )}
    </div>
  );
};

const TableView = (props: IProps) => {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const {
    data,
    columns,
    globalFilter,
    setGlobalFilter,
    noDataMessage,
    newlyCreatedAlertId,
    singleRowSelection,
    assignSuccessfully,
    setAssignSuccessfully,
    shouldResetSelection,
    acknowledgeReset,
    mode = TABLE_RENDERING_MODES.CLIENT_SIDE,
    totalCount,
    pageIndex,
    pageSize,
    onPaginationChange,
    renderFiltersAboveTable = false,
  } = props;

  /**
   * At times, the data is coming null or undefined. In such cases, we need to handle it.
   * TODO : Need to check the type
   */
  /*
    we need to check why we are getting undefined in data
     const tableData = data || [];   // need to update the tableData logic
  */

  const [expanded, setExpanded] = React.useState<ExpandedState>(
    props.defaultExpanded ?? true,
  );

  useEffect(() => {
  if (props.resetExpandedRowsKey === undefined) {
    return;
  }

  setExpanded(props.defaultExpanded ?? true);
}, [props.resetExpandedRowsKey]);

  const table = useReactTable({
    data: data == null ? [] : data, // Handle both null and undefined
    columns,
    getRowId: (originalRow: any, index: number, parent?: any) => {
      const stableRowId =
        originalRow?.alertHistoryId ||
        originalRow?._id ||
        originalRow?.id ||
        originalRow?.alertId ||
        originalRow?.groupId ||
        index.toString();

      return parent ? `${parent.id}.${stableRowId}` : stableRowId;
    },
    manualPagination: mode === TABLE_RENDERING_MODES.SERVER_SIDE,
    manualFiltering: mode === TABLE_RENDERING_MODES.SERVER_SIDE,
    pageCount:
      mode === TABLE_RENDERING_MODES.SERVER_SIDE
        ? Math.ceil(totalCount / pageSize)
        : undefined,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      ...(mode === TABLE_RENDERING_MODES.SERVER_SIDE && {
        pagination: {
          pageIndex: pageIndex ?? 0,
          pageSize: pageSize ?? 50,
        },
        rowCount: totalCount,
        globalFilter: props.globalFilter ?? "",
        columnFilters: props.columnFilters ?? [],
      }),
      ...(mode === TABLE_RENDERING_MODES.CLIENT_SIDE && {
        globalFilter,
        columnFilters,
      }),
      expanded,
      sorting,
    },
    onExpandedChange: setExpanded,
    getSubRows: (row: any) => row.subRows, //Todo: Need to check the type
    onColumnFiltersChange:
      mode === TABLE_RENDERING_MODES.SERVER_SIDE
        ? props.onColumnFiltersChange
        : setColumnFilters,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel:
      mode === TABLE_RENDERING_MODES.CLIENT_SIDE
        ? getFilteredRowModel()
        : undefined,
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel:
      mode === TABLE_RENDERING_MODES.CLIENT_SIDE
        ? getPaginationRowModel()
        : undefined,
    onSortingChange: setSorting,
    ...(mode === TABLE_RENDERING_MODES.SERVER_SIDE && {
      onPaginationChange: (updater) => {
        const current = table.getState().pagination;

        const next = typeof updater === "function" ? updater(current) : updater;
        if (
          next.pageIndex !== current.pageIndex ||
          next.pageSize !== current.pageSize
        ) {
          props.onPaginationChange?.(next.pageIndex, next.pageSize);
        }
      },
    }),
    enableSortingRemoval: false,
    enableMultiRowSelection: !singleRowSelection,
  });

  useEffect(() => {
    if (assignSuccessfully) {
      table.toggleAllRowsSelected(false);
      setAssignSuccessfully(false);
    }
  }, [assignSuccessfully]);

  useEffect(() => {
    if (shouldResetSelection) {
      table.toggleAllRowsSelected(false);
      acknowledgeReset?.();
    }
  }, [shouldResetSelection]);
  const isDataEmpty: boolean = table.getRowModel().rows.length === 0;

  return (
    <>
      {renderFiltersAboveTable &&
        (() => {
          const filterableHeaders =
            table.getHeaderGroups()[0]?.headers.filter((header) =>
              header.column.getCanFilter(),
            ) ?? [];

          if (filterableHeaders.length === 0) {
            return null;
          }

          const leadingFilters =
            filterableHeaders.length > 1
              ? filterableHeaders.slice(0, -1)
              : filterableHeaders;
          const trailingFilter =
            filterableHeaders.length > 1
              ? filterableHeaders[filterableHeaders.length - 1]
              : null;

          return (
            <div className="table-filter-bar">
              <div className="table-filter-bar-group">
                {leadingFilters.map((header) => (
                  <div className="alerts-dropdown" key={header.id}>
                    <Filter
                      column={header.column}
                      flatRows={table.getPreFilteredRowModel().flatRows}
                    />
                  </div>
                ))}
              </div>
              {trailingFilter && (
                <div className="alerts-dropdown" key={trailingFilter.id}>
                  <Filter
                    column={trailingFilter.column}
                    flatRows={table.getPreFilteredRowModel().flatRows}
                  />
                </div>
              )}
            </div>
          );
        })()}
      <div className="table-container">
        <div className="alert-table-container">
        <table>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <th key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder ? null : (
                      <>
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? "cursor-pointer select-none"
                              : "",
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </div>
                        {!renderFiltersAboveTable &&
                        header.column.getCanFilter() ? (
                          <div className="alerts-dropdown">
                            <Filter
                              column={header.column}
                              flatRows={table.getPreFilteredRowModel().flatRows}
                            />
                          </div>
                        ) : null}
                      </>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        {!isDataEmpty ? (
          <tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <tr
                  key={row.id}
                  className={[
                    props.getRowClassName?.(row),
                    row.id == newlyCreatedAlertId ? "highlighted" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        ) : (
          <tbody>
            <tr>
              <td className="empty-data-message">{noDataMessage}</td>
            </tr>
          </tbody>
        )}
      </table>
      {!isDataEmpty && (
        <div className="pagination-container">
          <div className="pagination-content">
            <div className="dm-page-input">
              <span>Item per page</span>
              <Form.Select
                aria-label="Default select example"
                value={table.getState().pagination.pageSize}
                onChange={(e) => {
                  const newSize = Number(e.target.value);

                  if (mode === TABLE_RENDERING_MODES.SERVER_SIDE) {
                    props.onPaginationChange?.(0, newSize);
                  } else {
                    table.setPageSize(newSize);
                  }
                }}
              >
                {[5, 10, 25, 50].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="dm-page-items">
              <span className="flex items-center gap-1">
                <div>Page</div>
                <strong>
                  {table.getState().pagination.pageIndex + 1} of{" "}
                  {table.getPageCount()}
                </strong>
              </span>
            </div>
          </div>

          <div className="dm-pagination">
            <button
              onClick={() => {
                const { pageIndex, pageSize } = table.getState().pagination;
                if (mode === TABLE_RENDERING_MODES.SERVER_SIDE) {
                  props.onPaginationChange?.(pageIndex - 1, pageSize);
                } else {
                  table.previousPage();
                }
              }}
              disabled={!table.getCanPreviousPage()}
            >
              <LeftPaginationIcon />
            </button>
            <button
              onClick={() => {
                const { pageIndex, pageSize } = table.getState().pagination;
                if (mode === TABLE_RENDERING_MODES.SERVER_SIDE) {
                  props.onPaginationChange?.(pageIndex + 1, pageSize);
                } else {
                  table.nextPage();
                }
              }}
              disabled={!table.getCanNextPage()}
            >
              <RightPaginationIcon />
            </button>
          </div>
       </div>
      )}
        </div>
      </div>
    </>
  );
};

export default TableView;

